import {Icon,type IconName} from './Icon';
import { AdvancedDetails } from './AdvancedDetails';
import {Link} from 'react-router';

export type ReadinessOutcome = 'met' | 'blocked' | 'unknown';
export type WorkflowSeverity = 'Low' | 'Medium' | 'High' | 'Critical';

export type AcceptedOperationalGap = Readonly<{
  acceptanceReason: string;
  acceptedAt: string;
  acceptedBy: string;
  approvalReference: string;
  blocksComplianceGreen: boolean;
  condition: string;
  consequence: string;
  evidenceReferences: ReadonlyArray<string>;
  expiresAt: string;
  mitigation: string;
  owner: string;
  reviewDate: string;
  schema: 'accepted-operational-gap/v1';
  severity: Exclude<WorkflowSeverity, 'Critical'>;
  targetDate: string | null;
}>;

export type BlockerItem = Readonly<{
  action?: Readonly<{href:string;label:string}> | undefined;
  acceptedGap?: AcceptedOperationalGap | undefined;
  blocksProgress: boolean;
  consequence?: string | undefined;
  detail?: string | undefined;
  evidence?: ReadonlyArray<string> | undefined;
  id: string;
  nextAction?: string | undefined;
  nonWaivable?: boolean | undefined;
  outcome: ReadinessOutcome;
  owner?: string | undefined;
  severity?: WorkflowSeverity | undefined;
  targetDate?: string | null | undefined;
  title: string;
}>;

export type BlockerPanelProps = Readonly<{
  ariaLabel: string;
  description?: string | undefined;
  items: ReadonlyArray<BlockerItem>;
  title?: string | undefined;
}>;

const outcomeLabels: Record<ReadinessOutcome, string> = {
  blocked: 'Blocked',
  met: 'Met',
  unknown: 'Unknown',
};

const outcomeSymbols: Record<ReadinessOutcome, IconName> = {
  blocked: 'circle-alert',
  met: 'circle-check',
  unknown: 'circle-question-mark',
};

function countLabel(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function GapDetails({ gap }: Readonly<{ gap: AcceptedOperationalGap }>) {
  return <div className="blocker-panel__gap">
    <p className="blocker-panel__gap-label">Accepted operational gap · not a completed prerequisite</p>
    <dl className="blocker-panel__facts">
      <div><dt>Condition</dt><dd>{gap.condition}</dd></div>
      <div><dt>Consequence</dt><dd>{gap.consequence}</dd></div>
      <div><dt>Mitigation</dt><dd>{gap.mitigation}</dd></div>
      <div><dt>Owner</dt><dd>{gap.owner}</dd></div>
      <div><dt>Target date</dt><dd>{gap.targetDate ?? 'No target date recorded'}</dd></div>
      <div><dt>Review date</dt><dd>{gap.reviewDate}</dd></div>
      <div><dt>Expires</dt><dd><time dateTime={gap.expiresAt}>{gap.expiresAt}</time></dd></div>
      <div><dt>Severity</dt><dd>{gap.severity}</dd></div>
    </dl>
    {gap.blocksComplianceGreen && <p className="blocker-panel__green-warning">This accepted gap prevents a green compliance conclusion.</p>}
    <AdvancedDetails summary="Acceptance evidence and decision">
      <dl className="blocker-panel__facts">
        <div><dt>Acceptance reason</dt><dd>{gap.acceptanceReason}</dd></div>
        <div><dt>Accepted by</dt><dd>{gap.acceptedBy}</dd></div>
        <div><dt>Accepted at</dt><dd><time dateTime={gap.acceptedAt}>{gap.acceptedAt}</time></dd></div>
        <div><dt>Approval reference</dt><dd>{gap.approvalReference}</dd></div>
      </dl>
      <p><strong>Evidence references</strong></p>
      <ul>{gap.evidenceReferences.map((reference) => <li key={reference}>{reference}</li>)}</ul>
    </AdvancedDetails>
  </div>;
}

export function BlockerPanel({ ariaLabel, description, items, title = 'Prerequisites and blockers' }: BlockerPanelProps) {
  const blocked = items.filter((item) => item.outcome === 'blocked' || Boolean(item.acceptedGap && item.nonWaivable)).length;
  const unknown = items.filter((item) => item.outcome === 'unknown').length;
  const acceptedGaps = items.filter((item) => Boolean(item.acceptedGap)).length;
  const summary = [
    blocked ? countLabel(blocked, 'blocked item', 'blocked items') : undefined,
    unknown ? countLabel(unknown, 'unknown item', 'unknown items') : undefined,
    acceptedGaps ? countLabel(acceptedGaps, 'accepted gap', 'accepted gaps') : undefined,
  ].filter((value): value is string => Boolean(value)).join(' · ') || (items.length?'Every supplied prerequisite is met':'Readiness information unavailable');

  return <section className="blocker-panel" aria-label={ariaLabel}>
    <header className="blocker-panel__header">
      <div>
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      <span className="blocker-panel__summary">{summary}</span>
    </header>
    {items.length === 0
      ? <p className="blocker-panel__empty">No prerequisite information was supplied.</p>
      : <ul className="blocker-panel__list">
        {items.map((item) => {
          const invalidGap = Boolean(item.acceptedGap && item.nonWaivable);
          const visualState = invalidGap ? 'blocked' : item.acceptedGap ? 'accepted-gap' : item.outcome;
          const stateLabel = invalidGap
            ? 'Blocked — accepted gaps cannot satisfy this non-waivable requirement'
            : item.acceptedGap ? 'Accepted gap' : outcomeLabels[item.outcome];
          return <li className={`blocker-panel__item blocker-panel__item--${visualState}`} data-outcome={item.outcome} key={item.id}>
            <span className="blocker-panel__icon" aria-hidden="true"><Icon name={invalidGap ? 'circle-alert' : item.acceptedGap ? 'triangle-alert' : outcomeSymbols[item.outcome]}/></span>
            <div className="blocker-panel__body">
              <div className="blocker-panel__title-row">
                <h3>{item.title}</h3>
                <span className="blocker-panel__state">{stateLabel}</span>
                {item.nonWaivable && <span className="blocker-panel__non-waivable">Non-waivable</span>}
                {item.blocksProgress && <span className="blocker-panel__blocking">Blocks progress</span>}
              </div>
              {item.detail && <p>{item.detail}</p>}
              {(item.severity || item.owner || item.targetDate !== undefined || item.evidence?.length) && <dl className="blocker-panel__facts">
                {item.severity && <div><dt>Severity</dt><dd>{item.severity}</dd></div>}
                {item.owner && <div><dt>Owner</dt><dd>{item.owner}</dd></div>}
                {item.targetDate !== undefined && <div><dt>Target</dt><dd>{item.targetDate ?? 'Not recorded'}</dd></div>}
                {item.evidence?.length && <div><dt>Evidence</dt><dd>{item.evidence.join(', ')}</dd></div>}
              </dl>}
              {item.consequence && <p className="blocker-panel__consequence"><strong>Consequence:</strong> {item.consequence}</p>}
              {item.nextAction && <p className="blocker-panel__next-action"><strong>Next action:</strong> {item.nextAction}</p>}
              {item.action && <p><Link to={item.action.href}>{item.action.label}</Link></p>}
              {item.acceptedGap && <GapDetails gap={item.acceptedGap} />}
            </div>
          </li>;
        })}
      </ul>}
  </section>;
}
